import { Grid, MenuItem, Select, TextField } from '@material-ui/core'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import { ClosableDialog } from 'components/controls/dialogs'
import { Button, CircularLoading, ClickableBox } from 'components/facets'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import { useUser } from 'components/providers/UserProvider'
import { useBuyPriceReferences } from 'lib/hooks/useBuyPriceReferences'
import { useFetchPurchaseEvents } from 'lib/hooks/useFetchPurchaseEvents'
import { getCurrencySymbol, scaleBuyPricesToBudget } from 'lib/utils'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table } from 'react-virtualized'
import {
  InventoryArticle,
  LinkArticleToPurchaseEvent,
  PriceStategyTarget,
  PurchaseEvent,
  roundTo2Decimals,
} from 'shared'
import { CancellationToken, newCancellationToken } from 'utils/cancellationToken'
import { PricingStrategyBox } from '../stockPricing/PricingSettings/PricingStrategyBox'
import { PurchaseEventCreation } from './PurchaseEventCreation'

type WizardStep = 'select' | 'pricing' | 'recap'
type SelectMode = 'existing' | 'new'

interface RecapRow {
  ptProductId: number
  representativeArticle: InventoryArticle
  quantity: number
  buyPrice: number
  pricingFailed: boolean
}

interface PurchaseEventWizardProps {
  open: boolean
  onClose: () => void
  articles: InventoryArticle[]
  onConfirm: (pe: PurchaseEvent, linkData: LinkArticleToPurchaseEvent) => Promise<void> | void
}

const aggregateByPtProduct = (articles: InventoryArticle[]): RecapRow[] => {
  const map = new Map<number, RecapRow>()
  for (const a of articles) {
    const key = a.ptProductId
    if (key == null) continue
    const existing = map.get(key)
    if (existing) {
      existing.quantity += a.quantity ?? 0
    } else {
      map.set(key, {
        ptProductId: key,
        representativeArticle: a,
        quantity: a.quantity ?? 0,
        buyPrice: 0,
        pricingFailed: false,
      })
    }
  }
  return Array.from(map.values())
}

export const PurchaseEventWizard = ({
  open,
  onClose,
  articles,
  onConfirm,
}: PurchaseEventWizardProps): JSX.Element => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { enqueueSnackbar } = useSnackbar()
  const { purchaseEvents, fetchPurchaseEvents, loading: loadingPEs } = useFetchPurchaseEvents()
  const fetchBuyReferences = useBuyPriceReferences()

  const competitors = user?.competitors ?? []
  const competitionDisabled =
    user?.subscription.featuresDisabled.includes('stockPricing.competitionPricing') ?? false
  const buyPricingStrategies =
    user?.pricingStrategies.filter((s) => s.target === PriceStategyTarget.buyPrice) ?? []

  const [step, setStep] = useState<WizardStep>('select')
  const [mode, setMode] = useState<SelectMode>('existing')
  const [selectedPE, setSelectedPE] = useState<PurchaseEvent | null>(null)

  const [chosenStrategyId, setChosenStrategyId] = useState('')
  const [totalBudget, setTotalBudget] = useState<number | null>(null)
  const [isCalculating, setIsCalculating] = useState(false)
  const [progress, setProgress] = useState(0)

  const [rows, setRows] = useState<RecapRow[]>([])
  const [buyPricesEditable, setBuyPricesEditable] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const cancellationTokenRef = useRef<CancellationToken | null>(null)

  useEffect(() => {
    if (!open) {
      cancellationTokenRef.current?.cancel()
      cancellationTokenRef.current = null
      setStep('select')
      setMode('existing')
      setSelectedPE(null)
      setChosenStrategyId('')
      setTotalBudget(null)
      setRows([])
      setBuyPricesEditable(true)
      setProgress(0)
      setIsCalculating(false)
    }
  }, [open])

  useEffect(() => {
    if (open && mode === 'existing' && purchaseEvents.length === 0) {
      fetchPurchaseEvents(1)
    }
  }, [open, mode, purchaseEvents.length, fetchPurchaseEvents])

  useEffect(() => {
    if (selectedPE?.price) setTotalBudget(selectedPE.price)
  }, [selectedPE])

  const chosenStrategy = buyPricingStrategies.find((s) => s._id === chosenStrategyId)

  const handleSelectExistingPE = (peId: number) => {
    const pe = purchaseEvents.find((p) => p.id === peId)
    if (pe) setSelectedPE(pe)
  }

  const handleAdvanceFromSelect = () => {
    if (!selectedPE) return
    setRows(aggregateByPtProduct(articles))
    setStep('pricing')
  }

  const handleLotCreated = (lot: PurchaseEvent) => {
    setSelectedPE(lot)
    setRows(aggregateByPtProduct(articles))
    setStep('pricing')
  }

  const handleSkipStrategy = () => {
    setRows(aggregateByPtProduct(articles))
    setBuyPricesEditable(false)
    setStep('recap')
  }

  const buildRowsFromReferencePrices = (refByArticleId: Record<string, number>): RecapRow[] => {
    const aggregated = aggregateByPtProduct(articles)

    const refByPtProductId = new Map<number, { sum: number; count: number }>()
    for (const article of articles) {
      const ref = refByArticleId[article.id!]
      if (ref == null || article.ptProductId == null) continue
      const entry = refByPtProductId.get(article.ptProductId) ?? { sum: 0, count: 0 }
      entry.sum += ref
      entry.count += 1
      refByPtProductId.set(article.ptProductId, entry)
    }

    const refByRow = new Map<number, number>()
    for (const row of aggregated) {
      const entry = refByPtProductId.get(row.ptProductId)
      if (entry && entry.count > 0) refByRow.set(row.ptProductId, entry.sum / entry.count)
    }

    if (totalBudget != null && totalBudget > 0) {
      const scaleInputs: { id: number; refPrice: number; quantity: number }[] = []
      for (const row of aggregated) {
        const ref = refByRow.get(row.ptProductId)
        if (ref == null || row.quantity <= 0) continue
        scaleInputs.push({ id: row.ptProductId, refPrice: ref, quantity: row.quantity })
      }
      const buyPriceById = new Map(
        scaleBuyPricesToBudget(scaleInputs, totalBudget).map((r) => [r.id, r.buyPrice])
      )

      if (buyPriceById.size > 0) {
        return aggregated.map((row) => {
          const ref = refByRow.get(row.ptProductId)
          if (ref == null) return { ...row, buyPrice: 0, pricingFailed: true }
          if (row.quantity <= 0) return { ...row, buyPrice: 0, pricingFailed: false }
          const buyPrice = buyPriceById.get(row.ptProductId)
          return { ...row, buyPrice: buyPrice ?? 0, pricingFailed: false }
        })
      }
    }

    return aggregated.map((row) => {
      const ref = refByRow.get(row.ptProductId)
      if (ref == null) return { ...row, buyPrice: 0, pricingFailed: true }
      return { ...row, buyPrice: roundTo2Decimals(ref), pricingFailed: false }
    })
  }

  const handleCalculate = async () => {
    if (!chosenStrategy) return
    cancellationTokenRef.current?.cancel()
    const token = newCancellationToken()
    cancellationTokenRef.current = token
    setIsCalculating(true)
    setProgress(0)
    try {
      const refByArticleId = await fetchBuyReferences({
        articles,
        strategy: chosenStrategy,
        cancellationToken: token,
        onProgress: setProgress,
      })
      if (token.cancelled) return
      setRows(buildRowsFromReferencePrices(refByArticleId))
      setBuyPricesEditable(true)
      setStep('recap')
    } catch (e) {
      if (token.cancelled) return
      enqueueSnackbar(t('error.autopricing'), { variant: 'error' })
    } finally {
      if (!token.cancelled) setIsCalculating(false)
    }
  }

  // const updateRowQuantity = (ptProductId: number, value: string) => {
  //   setRows((prev) =>
  //     prev.map((r) =>
  //       r.ptProductId === ptProductId ? { ...r, quantity: value === '' ? 0 : Number(value) } : r
  //     )
  //   )
  // }

  const updateRowBuyPrice = (ptProductId: number, value: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.ptProductId === ptProductId ? { ...r, buyPrice: value === '' ? 0 : Number(value) } : r
      )
    )
  }

  const recapTotal = rows.reduce((sum, r) => sum + r.buyPrice * r.quantity, 0)
  const hasInvalidRow = rows.some(
    (r) => !Number.isFinite(r.quantity) || !Number.isFinite(r.buyPrice) || r.quantity < 0
  )

  const handleConfirm = async () => {
    if (!selectedPE || hasInvalidRow) return
    const linkData: LinkArticleToPurchaseEvent = {}
    for (const row of rows) {
      if (row.quantity <= 0) continue
      linkData[row.ptProductId] = {
        quantity: row.quantity,
        withVat: selectedPE.withVat!,
        // backend stores the line total in purchase.price; unit price is derived on read
        buyPrice: roundTo2Decimals(row.buyPrice * row.quantity),
      } as LinkArticleToPurchaseEvent[number]
    }
    setSubmitting(true)
    try {
      await onConfirm(selectedPE, linkData)
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  const renderSelectStep = (): JSX.Element => (
    <div className="p-3">
      <h3 className="mt-0 mb-3">{t('taxation.wizard.selectOrCreatePe')}</h3>

      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={(_e, value) => {
          if (value !== null) {
            setMode(value)
            setSelectedPE(null)
          }
        }}
        size="small"
        className="mb-3"
      >
        <ToggleButton value="existing" data-testid="wizard-mode-existing">
          {t('taxation.wizard.existingPe')}
        </ToggleButton>
        <ToggleButton value="new" data-testid="wizard-mode-new">
          {t('taxation.wizard.newPe')}
        </ToggleButton>
      </ToggleButtonGroup>

      {mode === 'existing' && (
        <div>
          {loadingPEs ? (
            <CircularLoading />
          ) : purchaseEvents.length === 0 ? (
            <p>{t('taxation.noPeAvailables')}</p>
          ) : (
            <Select
              displayEmpty
              value={selectedPE?.id ?? ''}
              onChange={(e) => handleSelectExistingPE(Number(e.target.value))}
              data-testid="wizard-select-existing-pe"
              style={{ minWidth: 'min(360px, 100%)', maxWidth: 480 }}
            >
              <MenuItem value="" disabled>
                {t('taxation.selectPurchaseEvent')}
              </MenuItem>
              {purchaseEvents
                .filter((p) => !p.seller?.name?.includes('Listing'))
                .map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.lotNumber} - {p.seller?.name} - {p.price} €
                  </MenuItem>
                ))}
            </Select>
          )}
        </div>
      )}

      {mode === 'new' && <PurchaseEventCreation onLotCreated={handleLotCreated} />}

      {mode === 'existing' && (
        <div className="d-flex justify-content-end mt-4">
          <Button variant="outlined" className="me-3" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={!selectedPE}
            onClick={handleAdvanceFromSelect}
            data-testid="wizard-next-from-select"
          >
            {t('common.next')}
          </Button>
        </div>
      )}
    </div>
  )

  const renderPricingStep = (): JSX.Element => {
    if (isCalculating) {
      return (
        <div className="p-5 d-flex justify-content-center">
          {progress === 100 ? (
            t('autopriceArticles.finishing')
          ) : (
            <CircularLoading value={progress} />
          )}
        </div>
      )
    }

    if (!chosenStrategy) {
      const availableStrategies = buyPricingStrategies.filter(
        ({ _id, base }) =>
          !_id.includes('bulk') && !(competitionDisabled && base.kind === 'articleData')
      )
      return (
        <div className="p-3">
          <h3 className="mt-0 mb-3">{t('taxation.wizard.chooseBuyStrategy')}</h3>
          <p>
            {t('taxation.wizard.buyStrategyOptional', {
              currency: getCurrencySymbol(user?.currency ?? 'EUR'),
            })}
          </p>
          {availableStrategies.length === 0 ? (
            <p className="text-muted">{t('autopriceArticles.noBuyStrategies')}</p>
          ) : (
            <>
              <p>{t('autopriceArticles.chooseStrategy')}:</p>
              <Grid container>
                {availableStrategies.map((s) => (
                  <Grid item xs={12} md="auto" key={s._id}>
                    <ClickableBox
                      onClick={() => setChosenStrategyId(s._id)}
                      width="500px"
                      maxWidth="100%"
                      data-testid={`wizard-strategy-${s._id}`}
                    >
                      <PricingStrategyBox pricingStrategy={s} competitors={competitors} />
                    </ClickableBox>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
          <div className="d-flex justify-content-end mt-4">
            <Button variant="outlined" className="me-3" onClick={() => setStep('select')}>
              {t('common.back')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSkipStrategy}
              data-testid="wizard-skip-strategy-btn"
            >
              {t('taxation.wizard.continueWithoutStrategy')}
            </Button>
          </div>
        </div>
      )
    }

    return (
      <div className="p-3">
        <h3 className="mt-0 mb-3">{t('taxation.wizard.setBudget')}</h3>
        <p>
          {articles.length} {t('autopriceArticles.willBeAutopriced')} <b>{chosenStrategy.name}</b>
        </p>
        <p>{t('autopriceArticles.totalBuyBudgetHint')}</p>
        <div style={{ maxWidth: 300 }}>
          <TextField
            label={t('autopriceArticles.totalBuyBudget')}
            type="number"
            fullWidth
            value={totalBudget ?? ''}
            onChange={(e) => setTotalBudget(e.target.value === '' ? null : Number(e.target.value))}
            InputProps={{
              inputProps: { min: 0, step: 0.01, 'data-testid': 'wizard-total-budget' },
            }}
          />
        </div>
        <div className="d-flex justify-content-end mt-4">
          <Button variant="outlined" className="me-3" onClick={() => setChosenStrategyId('')}>
            {t('common.back')}
          </Button>
          <Button
            variant="contained"
            disabled={totalBudget !== null && totalBudget <= 0}
            onClick={handleCalculate}
            data-testid="wizard-calculate-btn"
          >
            {t('autopriceArticles.calculateBuyPrices')}
          </Button>
        </div>
      </div>
    )
  }

  const renderRecapStep = (): JSX.Element => (
    <div className="p-3">
      <h3 className="mt-0 mb-3">{t('taxation.wizard.recapTitle')}</h3>
      <p>
        {buyPricesEditable
          ? t('taxation.wizard.recapHint')
          : t('taxation.wizard.recapNoBuyPriceHint', {
              currency: getCurrencySymbol(user?.currency ?? 'EUR'),
            })}
      </p>

      <div style={{ height: 480 }}>
        <AutoSizer>
          {({ width, height }) => (
            <Table
              width={Math.max(width, 900)}
              height={height}
              headerHeight={40}
              rowHeight={64}
              rowCount={rows.length}
              rowGetter={({ index }) => rows[index]}
              className="picking-table"
            >
              <Column
                width={260}
                flexGrow={1}
                disableSort
                label={t('card.name')}
                dataKey="name"
                cellRenderer={({ rowData }) => {
                  const article = rowData.representativeArticle
                  return (
                    <div className="d-flex flex-column text-wrap">
                      <span>
                        {rowData.pricingFailed && (
                          <i
                            className="fa-solid fa-triangle-exclamation me-2"
                            style={{ color: 'var(--warning-color, #f0ad4e)' }}
                            title={t('taxation.wizard.pricingFailedHint')}
                          />
                        )}
                        {article.card.name}
                      </span>
                    </div>
                  )
                }}
              />

              <Column
                width={180}
                flexGrow={1}
                disableSort
                label={t('card.expansion')}
                dataKey="expansion"
                cellRenderer={({ rowData }) => (
                  <div className="d-flex flex-column text-wrap">
                    <span>{rowData.representativeArticle.card.expansion}</span>
                    <span style={{ fontSize: '0.85em', opacity: 0.7 }}>
                      #{rowData.representativeArticle.card.cn} -{' '}
                      {rowData.representativeArticle.card.set}
                    </span>
                  </div>
                )}
              />

              <Column
                width={180}
                disableSort
                label={t('transaction.properties')}
                dataKey="properties"
                cellRenderer={({ rowData }) => (
                  <ArticleAttributes article={rowData.representativeArticle} iconHeight={17} />
                )}
              />
              <Column
                width={120}
                disableSort
                label={t('card.quantity')}
                headerClassName="centered-header"
                dataKey="quantity"
                cellRenderer={({ rowData }) => (
                  <div className="w-100 text-center">
                    <span>{rowData.quantity}</span>
                  </div>
                  // <TextField
                  //   type="number"
                  //   value={rowData.quantity}
                  //   onChange={(e) => updateRowQuantity(rowData.ptProductId, e.target.value)}
                  //   InputProps={{
                  //     inputProps: {
                  //       min: 0,
                  //       step: 1,
                  //       'data-testid': `wizard-qty-${rowData.ptProductId}`,
                  //     },
                  //   }}
                  // />
                )}
              />
              {buyPricesEditable && (
                <Column
                  width={140}
                  disableSort
                  label={t('taxation.wizard.unitBuyPrice')}
                  dataKey="buyPrice"
                  cellRenderer={({ rowData }) => (
                    <TextField
                      type="number"
                      value={rowData.buyPrice}
                      onChange={(e) => updateRowBuyPrice(rowData.ptProductId, e.target.value)}
                      InputProps={{
                        inputProps: {
                          min: 0,
                          step: 0.01,
                          'data-testid': `wizard-price-${rowData.ptProductId}`,
                        },
                      }}
                    />
                  )}
                />
              )}
              <Column
                width={120}
                disableSort
                label={t('taxation.wizard.lineTotal')}
                dataKey="lineTotal"
                headerClassName="centered-header"
                cellRenderer={({ rowData }) => (
                  <div className="text-end">
                    {roundTo2Decimals(rowData.buyPrice * rowData.quantity)}{' '}
                    {selectedPE && getCurrencySymbol(selectedPE.currency)}
                  </div>
                )}
              />
            </Table>
          )}
        </AutoSizer>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <b>
          {t('taxation.wizard.total')}:{' '}
          <span data-testid="wizard-recap-total">{roundTo2Decimals(recapTotal)}</span>{' '}
          {selectedPE && getCurrencySymbol(selectedPE.currency)}
        </b>
      </div>

      <div className="d-flex justify-content-end mt-4">
        <Button
          variant="outlined"
          className="me-3"
          onClick={() => setStep('pricing')}
          disabled={submitting}
        >
          {t('common.back')}
        </Button>
        <Button
          variant="contained"
          disabled={hasInvalidRow || submitting}
          onClick={handleConfirm}
          data-testid="wizard-confirm-btn"
        >
          {t('taxation.wizard.saveAndLink')}
        </Button>
      </div>
    </div>
  )

  return (
    <ClosableDialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      {step === 'select' && renderSelectStep()}
      {step === 'pricing' && renderPricingStep()}
      {step === 'recap' && renderRecapStep()}
    </ClosableDialog>
  )
}
